import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../api/axiosConfig";
import { RYDER_COUNTRY_CODES, getCleanPhone } from "../utils/phoneCountryCodes";
import { REQUEST_STATUS_VIEW } from "../utils/ryderRequestStatus";
import AvatarCapture from "../components/AvatarCapture";
import DocumentCapture from "../components/DocumentCapture";
import { checkDocumentPhoto } from "../utils/documentOcr";
import { toast } from "react-toastify";
import { ArrowLeft, User, Loader, CheckCircle, X, FileImage, Building2, Send, ChevronDown } from "lucide-react";

const VEHICLE_OPTIONS = [
  {
    type: "bicicleta",
    emoji: "🚲",
    label: "Bicicleta",
    description: "Ideal para entregas ligeras y rápidas en distancias cortas.",
    capacities: ["Hasta 10 kg", "10 – 20 kg"],
  },
  {
    type: "moto",
    emoji: "🛵",
    label: "Moto",
    description: "Perfecta para la mayoría de domicilios: rápida y ágil.",
    capacities: ["Hasta 20 kg", "20 – 50 kg", "50 – 100 kg"],
  },
  {
    type: "carro",
    emoji: "🚗",
    label: "Carro",
    description: "Mayor capacidad para pedidos voluminosos o múltiples envíos.",
    capacities: ["Hasta 100 kg", "100 – 300 kg", "300 – 500 kg"],
  },
  {
    type: "camion",
    emoji: "🚛",
    label: "Camión",
    description: "Carga pesada, mudanzas o distribución de gran volumen.",
    capacities: ["Hasta 1 tonelada", "1 – 3 toneladas", "3 – 5 toneladas", "Más de 5 toneladas"],
  },
  {
    type: "otro",
    emoji: "📦",
    label: "Otro",
    description: "Cualquier otro medio de transporte.",
    capacities: ["Hasta 20 kg", "20 – 50 kg", "50 – 100 kg"],
  },
];

export default function ConvertToRyder() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    if (!user) return navigate("/login");
  }, [user, navigate]);

  const [companies, setCompanies] = useState([]);
  const [deliveryCompanies, setDeliveryCompanies] = useState([]);
  const [formData, setFormData] = useState({
    phone: "",
    phoneCountryCode: "+58",
    documentType: "CC",
    documentNumber: "",
    company: "",
    vehicle: {
      type: "moto",
      plate: "",
      brand: "",
      model: "",
      capacity: "Hasta 20 kg",
    },
  });
  const [loading, setLoading] = useState(false);
  const [docPhoto, setDocPhoto] = useState(null);
  const [savedAccount, setSavedAccount] = useState(null);
  const [avatarFile, setAvatarFile]     = useState(null);

  const handleAvatar = (file) => setAvatarFile(file);

  useEffect(() => {
    axios.get("/auth/me").then((res) => {
      const me = res.data?.data?.user;
      if (!me) return;
      setSavedAccount(me);
      setFormData((prev) => ({
        ...prev,
        phone: me.phone || prev.phone,
        documentType: me.documentNumber ? me.documentType : prev.documentType,
        documentNumber: me.documentNumber || prev.documentNumber,
      }));
    }).catch(() => {});
  }, []);

  const [existingRequest, setExistingRequest] = useState(null);
  useEffect(() => {
    if (!user?.id) return;
    axios.get(`/ryders/user/${user.id}`)
      .then((res) => setExistingRequest(res.data?.data?.ryder ?? null))
      .catch(() => {});
  }, [user?.id]);

  // Delivery company application state
  const [applyToDelivery,        setApplyToDelivery]        = useState(false);
  const [selectedDeliveryCompany, setSelectedDeliveryCompany] = useState("");
  const [applicationMessage,     setApplicationMessage]     = useState("");

  const selectedVehicle = VEHICLE_OPTIONS.find((v) => v.type === formData.vehicle.type);

  useEffect(() => {
    fetchCompanies();
    fetchDeliveryCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const res = await axios.get("/companies/public?hasOwnDelivery=true");
      const list = res.data?.data?.data ?? res.data?.data ?? res.data ?? [];
      setCompanies(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Error al cargar empresas:", error);
    }
  };

  const fetchDeliveryCompanies = async () => {
    try {
      const res = await axios.get("/delivery-companies/public");
      const list = res.data?.data?.companies ?? res.data?.companies ?? [];
      setDeliveryCompanies(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Error al cargar empresas de domicilio:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("vehicle.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        vehicle: { ...prev.vehicle, [field]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const selectVehicleType = (type) => {
    const option = VEHICLE_OPTIONS.find((v) => v.type === type);
    setFormData((prev) => ({
      ...prev,
      vehicle: {
        ...prev.vehicle,
        type,
        capacity: option?.capacities[0] ?? "",
      },
    }));
  };

  const handleDocPhoto = (file) => setDocPhoto(file);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!docPhoto && !savedAccount?.documentPhoto) { toast.warn("Debes subir la foto de tu documento de identidad"); return; }
    if (!avatarFile) { toast.warn("Debes subir una foto de perfil de domiciliario; los clientes la verán al recibir tu pedido"); return; }
    setLoading(true);
    try {
      if (docPhoto) {
        const ocr = await checkDocumentPhoto(docPhoto, formData.documentNumber).catch(() => ({ ok: true }));
        if (!ocr.ok) { toast.warn(ocr.message); return; }
      }
      const data = new FormData();
      data.append("userId",         user.id);
      data.append("email",          user.email);
      data.append("name",           user.name);
      data.append("phone",          getCleanPhone(formData.phone, formData.phoneCountryCode).clean);
      data.append("phoneCountryCode", formData.phoneCountryCode);
      data.append("documentType",   formData.documentType);
      data.append("documentNumber", formData.documentNumber);
      data.append("company",        formData.company || "");
      data.append("vehicle",        JSON.stringify({
        type:     formData.vehicle.type,
        plate:    formData.vehicle.plate    || "",
        brand:    formData.vehicle.brand    || "",
        model:    formData.vehicle.model    || "",
        capacity: formData.vehicle.capacity || "",
      }));
      if (docPhoto) data.append("documentPhoto", docPhoto);
      if (avatarFile) data.append("avatar", avatarFile);

      const res = await axios.post("/ryders", data);

      // Role stays unchanged until admin approves — do not update localStorage role or ryderId yet
      toast.success("¡Solicitud enviada! Revisaremos tu información y te notificaremos cuando sea aprobada 🟡");
      navigate("/");
    } catch (error) {
      const msg = error.response?.data?.message || "Error al enviar la solicitud";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const canResend = existingRequest?.approvalStatus === "rechazado" && existingRequest.canResubmit;

  if (existingRequest && !canResend) {
    const view = REQUEST_STATUS_VIEW[existingRequest.approvalStatus] ?? REQUEST_STATUS_VIEW.pendiente;
    return (
      <div className="min-h-screen bg-gradient-to-br from-supernova-50 to-supernova-100 py-8 px-4 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-5 sm:p-8 max-w-md w-full text-center">
          <div className="text-4xl mb-3">{view.icon}</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">{view.title}</h1>
          <p className="text-gray-600 mb-4">{view.text}</p>
          {existingRequest.rejectionReason && (
            <div className="text-left bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <p className="text-sm font-semibold text-red-700 mb-1">Motivo</p>
              <p className="text-sm text-red-700 whitespace-pre-line">{existingRequest.rejectionReason}</p>
            </div>
          )}
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 rounded-xl bg-supernova-500 text-white font-bold hover:bg-supernova-600 transition"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-supernova-50 to-supernova-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-2 text-supernova-600 hover:text-supernova-700"
        >
          <ArrowLeft size={20} />
          <span>Volver</span>
        </button>

        {canResend && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-red-700">Tu solicitud anterior fue rechazada</p>
            <p className="text-sm text-red-700 whitespace-pre-line mt-1">{existingRequest.rejectionReason}</p>
            <p className="text-xs text-red-600 mt-2">Corrige lo necesario y vuelve a enviar tu solicitud.</p>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-xl p-5 sm:p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-supernova-100 rounded-full mb-4 text-3xl">
              {selectedVehicle?.emoji ?? "🛵"}
            </div>
            <h1 className="text-3xl font-bold text-gray-800">Convertirme en Domiciliario</h1>
            <p className="text-gray-600 mt-2">Completa la información necesaria. Tu solicitud será revisada.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-700 mb-1"><strong>Nombre:</strong> {user?.name}</p>
              <p className="text-sm text-gray-700"><strong>Email:</strong> {user?.email}</p>
              <p className="text-xs text-gray-500 mt-2">Estos datos vienen de tu cuenta y no pueden modificarse.</p>
            </div>

            {/* Información personal */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                <User size={20} /> Información Personal
              </h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono *</label>
                <div className="flex gap-2">
                  {!savedAccount?.phone && (
                    <select
                      name="phoneCountryCode" value={formData.phoneCountryCode} onChange={handleChange}
                      className="w-28 sm:w-40 shrink-0 px-2 sm:px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm"
                    >
                      {RYDER_COUNTRY_CODES.map((c) => (
                        <option key={c.code} value={c.code}>{c.flag} {c.code} {c.name}</option>
                      ))}
                    </select>
                  )}
                  <input
                    type="tel" name="phone" value={formData.phone} onChange={handleChange} required
                    readOnly={!!savedAccount?.phone}
                    className={`flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg ${savedAccount?.phone ? "bg-gray-100 text-gray-600" : ""}`}
                    placeholder="3001234567"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Documento *</label>
                  <select
                    name="documentType" value={formData.documentType} onChange={handleChange} required
                    disabled={!!savedAccount?.documentNumber}
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${savedAccount?.documentNumber ? "bg-gray-100 text-gray-600" : ""}`}
                  >
                    <option value="CC">Cédula de Ciudadanía</option>
                    <option value="CE">Cédula de Extranjería</option>
                    <option value="TI">Tarjeta de Identidad</option>
                    <option value="PASAPORTE">Pasaporte</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Número de Documento *</label>
                  <input
                    type="text" name="documentNumber" value={formData.documentNumber} onChange={handleChange} required
                    readOnly={!!savedAccount?.documentNumber}
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${savedAccount?.documentNumber ? "bg-gray-100 text-gray-600" : ""}`}
                    placeholder="1234567890"
                  />
                </div>
              </div>

              {/* Foto de perfil */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Foto de perfil de domiciliario *
                </label>
                <p className="text-xs text-gray-500 mb-2">Los clientes verán esta foto para reconocerte al recibir su pedido.</p>
                <AvatarCapture onAccept={handleAvatar} />
              </div>

              {/* Foto del documento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Foto del documento de identidad{savedAccount?.documentPhoto ? "" : " *"}
                </label>
                {savedAccount?.documentPhoto && !docPhoto && (
                  <p className="text-xs text-green-700 mb-2">Ya tienes una foto de tu documento guardada. Tómala o súbela solo si quieres reemplazarla.</p>
                )}
                <DocumentCapture onAccept={handleDocPhoto} />
                <p className="text-xs text-gray-500 mt-1">
                  Cédula o documento legal. Se verifica que el número coincida y se guarda de forma segura.
                </p>
              </div>
            </div>

            {/* Vehículo */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                <span className="text-xl">{selectedVehicle?.emoji}</span> Información del Vehículo
              </h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Vehículo *</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {VEHICLE_OPTIONS.map((v) => (
                    <button
                      key={v.type}
                      type="button"
                      onClick={() => selectVehicleType(v.type)}
                      className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all text-center ${
                        formData.vehicle.type === v.type
                          ? "border-supernova-500 bg-supernova-50 shadow-sm"
                          : "border-gray-200 hover:border-gray-300 bg-white"
                      }`}
                    >
                      <span className="text-3xl">{v.emoji}</span>
                      <span className="text-sm font-semibold text-gray-800">{v.label}</span>
                    </button>
                  ))}
                </div>

                {selectedVehicle && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-800">
                    <strong>{selectedVehicle.label}:</strong> {selectedVehicle.description}
                  </div>
                )}
              </div>

              {/* Capacidad de carga */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Capacidad de Carga *</label>
                <select
                  name="vehicle.capacity"
                  value={formData.vehicle.capacity}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  {selectedVehicle?.capacities.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  El administrador y las empresas podrán asignarte pedidos según tu capacidad declarada.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Placa (Opcional)</label>
                  <input
                    type="text" name="vehicle.plate" value={formData.vehicle.plate} onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="ABC123"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Marca (Opcional)</label>
                  <input
                    type="text" name="vehicle.brand" value={formData.vehicle.brand} onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Yamaha"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Modelo (Opcional)</label>
                  <input
                    type="text" name="vehicle.model" value={formData.vehicle.model} onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="FZ"
                  />
                </div>
              </div>
            </div>

            {/* Empresa propia de tienda */}
            {companies.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Empresa de tienda (Opcional)</label>
                <select
                  name="company" value={formData.company} onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Seleccionar empresa...</option>
                  {companies.map((company) => (
                    <option key={company.id ?? company._id} value={company.id ?? company._id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* ── Sección empresa de domicilio ── */}
            <div className="border border-indigo-200 rounded-2xl overflow-hidden">
              <button
                type="button"
                onClick={() => { setApplyToDelivery(v => !v); setSelectedDeliveryCompany(""); setApplicationMessage(""); }}
                className={`w-full flex items-center justify-between gap-3 px-5 py-4 text-left transition
                  ${applyToDelivery ? "bg-indigo-600 text-white" : "bg-indigo-50 hover:bg-indigo-100 text-indigo-800"}`}
              >
                <div className="flex items-center gap-3">
                  <Building2 size={18} className={applyToDelivery ? "text-white" : "text-indigo-600"} />
                  <div>
                    <p className="font-semibold text-sm">¿Quieres trabajar con una empresa de domicilios?</p>
                    <p className={`text-xs mt-0.5 ${applyToDelivery ? "text-indigo-200" : "text-indigo-500"}`}>
                      Envía tu solicitud al crear tu perfil
                    </p>
                  </div>
                </div>
                <ChevronDown
                  size={18}
                  className={`transition-transform shrink-0 ${applyToDelivery ? "rotate-180 text-white" : "text-indigo-400"}`}
                />
              </button>

              {applyToDelivery && (
                <div className="p-5 bg-white space-y-4 border-t border-indigo-100">
                  {deliveryCompanies.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-2">
                      No hay empresas de domicilio registradas en la plataforma por ahora.
                    </p>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Selecciona la empresa <span className="text-red-500">*</span>
                        </label>
                        <div className="grid grid-cols-1 gap-2 max-h-52 overflow-y-auto">
                          {deliveryCompanies.map((dc) => (
                            <button
                              key={dc.id}
                              type="button"
                              onClick={() => setSelectedDeliveryCompany(dc.id)}
                              className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition
                                ${selectedDeliveryCompany === dc.id
                                  ? "border-indigo-500 bg-indigo-50"
                                  : "border-gray-200 hover:border-indigo-300 hover:bg-gray-50"}`}
                            >
                              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                                {dc.name.slice(0, 2).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <p className="font-semibold text-gray-900 text-sm truncate">{dc.name}</p>
                                  {dc.verified && <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full shrink-0">✓ Verificada</span>}
                                </div>
                                <p className="text-xs text-gray-500 truncate">
                                  {dc.city && `${dc.city} · `}{dc._count?.ryders ?? 0} domiciliarios
                                </p>
                              </div>
                              {selectedDeliveryCompany === dc.id && (
                                <CheckCircle size={16} className="text-indigo-600 shrink-0" />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>

                      {selectedDeliveryCompany && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Mensaje para la empresa <span className="text-gray-400">(opcional)</span>
                          </label>
                          <textarea
                            value={applicationMessage}
                            onChange={(e) => setApplicationMessage(e.target.value)}
                            rows={2}
                            maxLength={400}
                            placeholder="Preséntate brevemente: experiencia, zona de cobertura, disponibilidad..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-400 outline-none resize-none"
                          />
                          <p className="text-xs text-gray-400 mt-0.5 text-right">{applicationMessage.length}/400</p>
                        </div>
                      )}

                      {selectedDeliveryCompany && (
                        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
                          <span className="text-lg leading-none shrink-0">⏳</span>
                          <p className="text-xs text-amber-800">
                            Tu solicitud quedará <strong>pendiente de aprobación</strong>. La empresa revisará tu perfil y te notificará.
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || (applyToDelivery && !selectedDeliveryCompany && deliveryCompanies.length > 0)}
              className="w-full bg-supernova-500 text-white py-3 rounded-lg font-semibold hover:bg-supernova-600 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
            >
              {loading ? (
                <><Loader className="animate-spin" size={20} /> Enviando...</>
              ) : applyToDelivery && selectedDeliveryCompany ? (
                <><Send size={18} /> Registrarme y enviar solicitud</>
              ) : (
                <><CheckCircle size={20} /> Enviar Solicitud</>
              )}
            </button>
            {applyToDelivery && !selectedDeliveryCompany && deliveryCompanies.length > 0 && (
              <p className="text-xs text-amber-600 text-center -mt-2">Selecciona una empresa de domicilio para continuar</p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
